/**
 * Semantic roles: components ask for a role, the theme decides the color (docs/UI_STANDARDS.md).
 * A role is never bypassed with a raw color in a page or feature component.
 */
export const semanticRoles = {
  /** The primary action color. */
  brand: "brand",
  /** Positive, completed, favorable, healthy, connected, live. */
  success: "teal",
  /** Operational caution; an exception that is not yet a failure. */
  warning: "orange",
  /** Negative, failed, loss, destructive, risk. */
  danger: "red",
  /** Informational, in progress, neutral notice. */
  info: "cyan",
  /** Rank, reward, champion, premium distinction only. Never a second brand color. */
  achievement: "yellow",
  /** Secondary special state that must not read as success, danger, or brand. */
  special: "violet",
  /** Ordinary information and passive chrome. */
  neutral: "gray",
} as const;
export type SemanticRole = keyof typeof semanticRoles;
export type SemanticColor = (typeof semanticRoles)[SemanticRole];

/** The Mantine color name for a semantic role, for components that take a `color` prop. */
export function semanticColor(role: SemanticRole): SemanticColor {
  return semanticRoles[role];
}

/**
 * Vivid tone in both schemes for inline styles and chart configuration: the darkened shade on light, the anchor on
 * dark, matching `primaryShade`.
 */
export function vivid(color: string): string {
  return `light-dark(var(--mantine-color-${color}-7), var(--mantine-color-${color}-5))`;
}

/** Muted tone for baselines, gridlines, and reference series. */
export function muted(color: string = "gray"): string {
  return `light-dark(var(--mantine-color-${color}-5), var(--mantine-color-${color}-6))`;
}

/** Direction of a change, so positive/negative never depends on color alone. */
export type Trend = "up" | "down" | "flat";

/** The semantic role a signed value takes. Unknown and zero values stay neutral, never green. */
export function trendRole(trend: Trend): SemanticRole {
  return trend === "up" ? "success" : trend === "down" ? "danger" : "neutral";
}
